import { Component } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { NgClass } from '@angular/common';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { ButtonComponent } from '../../../../shared/components/button/button.component';
import { InputComponent } from '../../../../shared/components/input/input.component';
import { IconComponent } from '../../../../shared/components/icon/icon.component';
import { ButtonTypeEnum } from '../../../../shared/components/button/types/button-type.enum';
import { ButtonSizeEnum } from '../../../../shared/components/button/types/button-size.enum';
import { ButtonRadiusEnum } from '../../../../shared/components/button/types/button-radius.enum';
import { ModalService } from '../../../../shared/services/modal/modal.service';
import { SuccessModalComponent } from '../../../../shared/modals/success-modal/success-modal.component';
import { sectors } from '../../../../shared/mocks/interfaces/sector.mock';
import { Sector } from '../../../../shared/models/interfaces/sector.interface';
import { SpinnerComponent } from '../../../../shared/components/spinner/spinner.component';
import { SpinnerColorEnum } from '../../../../shared/components/spinner/types/spinner-color.enum';
import { SpinnerSizeEnum } from '../../../../shared/components/spinner/types/spinner-size.enum';
import { SelectModule } from 'primeng/select';
import { User } from '../../../../shared/models/interfaces/user.interface';
import { UserService } from '../../../../shared/services/user2/user.service';
import { ToastService } from '../../../../shared/components/toast/service/toast.service';
import { getCrudErrorMessage } from '../../../../shared/utils/functions';

interface CreateUserForm {
  name: FormControl<string>;
  email: FormControl<string>;
  position: FormControl<string | undefined>;
  sectorId: FormControl<string | undefined>;
}

interface CreateUserModalData {
  mode?: 'create' | 'edit';
  user?: Partial<User>;
}

@Component({
  selector: 'l-create-user-modal',
  standalone: true,
  templateUrl: './create-user-modal.component.html',
  imports: [
    ReactiveFormsModule,
    NgClass,
    ButtonComponent,
    InputComponent,
    IconComponent,
    SpinnerComponent,
    SelectModule,
  ],
})
export class CreateUserModalComponent {

  constructor(
    private readonly ref: DynamicDialogRef,
    private readonly config: DynamicDialogConfig<CreateUserModalData>,
    private readonly modalService: ModalService,
    private readonly formBuilder: FormBuilder,
    private readonly userService: UserService,
    private readonly toastService: ToastService,
  ) { 
    this.formGroup = this.formBuilder.group<CreateUserForm>({
      name: new FormControl<string>('', { nonNullable: true, validators: [Validators.required] }),
      email: new FormControl<string>('', { nonNullable: true, validators: [Validators.required, Validators.email] }),
      position: new FormControl<string | undefined>(undefined, { nonNullable: true }),
      sectorId: new FormControl<string | undefined>(undefined, { nonNullable: true }),
    });

    if (this.isEditMode) {
      const sectorName = this.config.data?.user?.sector;
      const sectorId = sectorName
        ? this.sectors.find((sector) => sector.name === sectorName)?.id
        : undefined;

      this.formGroup.patchValue({
        name: this.config.data?.user?.name ?? '',
        email: this.config.data?.user?.email ?? '',
        position: this.config.data?.user?.position,
        sectorId,
      });
    }
  }

  public isSubmitting: boolean = false;

  public formGroup!: FormGroup<CreateUserForm>;

  public readonly buttonTypeEnum: typeof ButtonTypeEnum = ButtonTypeEnum;

  public readonly buttonSizeEnum: typeof ButtonSizeEnum = ButtonSizeEnum;

  public readonly buttonRadiusEnum: typeof ButtonRadiusEnum = ButtonRadiusEnum;

  public readonly spinnerColorEnum: typeof SpinnerColorEnum = SpinnerColorEnum;

  public readonly spinnerSizeEnum: typeof SpinnerSizeEnum = SpinnerSizeEnum;

  public readonly sectors: Sector[] = sectors;

  public get isEditMode(): boolean {
    return this.config.data?.mode === 'edit';
  }

  public get title(): string {
    return this.isEditMode ? 'Editar usuário' : 'Criar novo usuário';
  }

  public get subtitle(): string {
    return this.isEditMode
      ? 'Edite as informações do usuário'
      : 'Cadastre um novo usuário na plataforma';
  }

  public get illustrationAlt(): string {
    return this.isEditMode ? 'Editar usuário' : 'Criar usuário';
  }

  public get submitLabel(): string {
    return this.isEditMode ? 'Atualizar' : 'Criar';
  }

  public get nameControl(): FormControl {
    return this.formGroup.get('name') as FormControl;
  }

  public get emailControl(): FormControl {
    return this.formGroup.get('email') as FormControl;
  }

  public get sectorIdControl(): FormControl {
    return this.formGroup.get('sectorId') as FormControl;
  }

  public close(): void {
    this.ref.close();
  }

  public async submit(): Promise<void> {
    try {
      this.formGroup.markAllAsTouched();

      if (this.formGroup.invalid || this.isSubmitting) {
        return;
      }

      this.isSubmitting = true;
      const formValue = this.formGroup.getRawValue();
      const payload = {
        name: formValue.name,
        email: formValue.email,
        position: formValue.position,
        sectorId: formValue.sectorId,
      };

      if (this.isEditMode && this.config.data?.user?.id) {
        await this.userService.update(this.config.data.user.id, payload);
      } else {
        await this.userService.create(payload);
      }

      this.ref.close({ reload: true });

      this.modalService.open(SuccessModalComponent, {
        width: '500px',
        modal: true,
        closable: false,
        showHeader: false,
        data: {
          title: this.isEditMode ? 'Usuário atualizado com sucesso!' : 'Usuário criado com sucesso!',
          subtitle: this.isEditMode
            ? 'As alterações foram salvas e já estão disponíveis na lista de usuários.'
            : 'Enviaremos um e-mail para o endereço cadastrado com uma senha provisória e instruções para criação de uma nova senha.',
          imageAlt: this.isEditMode ? 'Usuário atualizado com sucesso' : 'Usuário criado com sucesso',
        },
      });
    } catch (error) {
      this.toastService.error(getCrudErrorMessage(error), 'Oops...');
    } finally {
      this.isSubmitting = false;
    }
  }

}
